import './SotGa4MappingTool.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Page } from '@Components/Page';
import { PAGE_STATE } from '@Components/Page/page-constants';
import { useSetError } from '../../redux/modals';
import {
  awaitJSON,
  fetchAndParseCachedCsv,
  fetchCometSelfInvoke,
  SegmentationMappingLambdaFetch
} from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { CustomSegmentsData } from '../SegmentationMapping/segmentation-mapping-constants';
import { SotGa4Labeling } from './SotGa4Labeling';
import { SotGa4MappingRow } from './sot-ga4-mapping-tool-constants';
import { sortSegments } from './sot-ga4-mapping-tool-utils';

const PageTab = {
  LABELING: 'labeling'
} as const;
type PageTabType = (typeof PageTab)[keyof typeof PageTab];

const PAGE_TAB_VALUES: readonly string[] = Object.values(PageTab);
const isPageTab = (key: string): key is PageTabType => PAGE_TAB_VALUES.includes(key);

const NAVS = [{ label: 'Labeling', key: PageTab.LABELING }];

export const SotGa4MappingTool = () => {
  const [loading, setLoading] = useState(false);
  const { tab, goToTab } = useTabbedNav<PageTabType>({
    baseURL: 'sot-ga4-mapping-tool',
    defaultKey: PageTab.LABELING,
    navs: NAVS.map(nav => nav.key)
  });
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const [customSegments, setCustomSegments] = useState<CustomSegmentsData[] | undefined>();
  const [rawData, setRawData] = useState<SotGa4MappingRow[] | null>(null);

  // Ignore responses that land after the user switched companies, and dedupe
  // concurrent fetches per (key, cid) — mirrors SegmentationMapping.tsx.
  const activeCid = useRef(cid);
  useEffect(() => {
    activeCid.current = cid;
  }, [cid]);

  const inflightFetches = useRef<Record<string, string>>({});
  const runGuarded = useCallback(
    (key: string, fetcher: () => Promise<unknown>) => {
      if (!cid || inflightFetches.current[key] === cid) return;
      inflightFetches.current[key] = cid;
      fetcher().finally(() => {
        if (inflightFetches.current[key] === cid) {
          delete inflightFetches.current[key];
        }
      });
    },
    [cid]
  );

  useEffect(() => {
    if (!cid) return;
    setRawData(null);
    setCustomSegments(undefined);
  }, [cid]);

  const fetchRawData = useCallback(async () => {
    try {
      let cacheKey: string;
      if (import.meta.env.DEV) {
        console.info('In development mode, fetching getRawAnalyticsData from non-comet endpoint');
        const res = await SegmentationMappingLambdaFetch('/getRawAnalyticsData', {
          params: { company: cid }
        });
        cacheKey = await awaitJSON<string>(res);
      } else {
        // GET: comet self-invoke route
        cacheKey = await fetchCometSelfInvoke<{ company: string }, string>(
          SegmentationMappingLambdaFetch,
          '/getRawAnalyticsData',
          { company: cid },
          { method: 'GET', noTimeout: true }
        );
      }

      const data = await fetchAndParseCachedCsv<SotGa4MappingRow>(cacheKey, {
        transformHeader: header => header.toLowerCase()
      });

      if (activeCid.current !== cid) return;
      setRawData(data.filter(row => row.campaign_key));
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  }, [cid, setError]);

  useEffect(() => {
    if (rawData) return;
    runGuarded('rawData', fetchRawData);
  }, [rawData, runGuarded, fetchRawData]);

  const fetchCustomSegments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await SegmentationMappingLambdaFetch('/getCustomSegments', {
        params: { company: cid }
      });
      const data = await awaitJSON<CustomSegmentsData[]>(res);
      if (activeCid.current !== cid) return;
      setCustomSegments(sortSegments(data));
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setLoading(false);
    }
  }, [cid, setError]);

  useEffect(() => {
    if (customSegments) return;
    runGuarded('customSegments', fetchCustomSegments);
  }, [customSegments, runGuarded, fetchCustomSegments]);

  return (
    <Page
      title='SOT/GA4 Mapping Tool'
      pageType='SOT/GA4 Mapping Tool'
      navs={NAVS}
      selectedNav={tab}
      isLoading={loading}
      state={loading ? PAGE_STATE.LOADING : undefined}
      onNav={key => {
        if (isPageTab(key)) {
          goToTab(key);
        }
      }}
    >
      <div className='sot-ga4-mapping-page' data-testid='sot-ga4-mapping-page'>
        <SotGa4Labeling
          data={rawData}
          customSegments={customSegments}
          fetchRawData={fetchRawData}
        />
      </div>
    </Page>
  );
};
